import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Paper, Chip, Select, MenuItem, Button,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert,
} from "@mui/material";
// Plain MUI Close (simple "X", no circle) — the established close-button
// icon everywhere else in the app (see KnowledgeSourcesTab.jsx's own header
// comment for the full writeup).
import CloseIcon from "@mui/icons-material/Close";
// Iconsax, matching the rest of this module's icon set (see
// icon-library-iconsax memory) — replaces the old plain MUI icons (Rahim,
// 2026-09-05, restyle pass to match Agents/Knowledge Sources).
import { Magicpen, MessageEdit, Cpu } from "iconsax-react";
// EmojiHappy/Translate/MessageQuestion (Sentiment Analysis, Auto
// Translation, Request a Feature icons) dropped from the import — those
// sections are commented out below (Rahim, 2026-09-05), re-add alongside
// them when re-enabled.
// The app's real notification system (see AiSettings.jsx's own toast
// import for the full writeup) — this tab's load/save errors were a plain
// inline Alert instead (Rahim, 2026-09-04 audit).
import { toast } from "react-hot-toast";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { SERVER_DOMAIN } from "../constants";
import { LabeledField, inboxSelectSx, inboxSelectMenuProps, inboxMenuItemSx } from "../components/LabeledField";
// ExpandableParagraphField only used by the commented-out "Request a
// Feature" section below (Rahim, 2026-09-05) — re-add when re-enabled.
import PillSwitch from "../components/PillSwitch";

// Same neutral bordered button every other page on this module uses ("never
// ever do a green button" — see KnowledgeSourcesTab.jsx/AgentsTab.jsx's own
// copy of this exact object).
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

// Iconsax icons don't accept the `sx` prop — every icon on this page is
// wrapped in a fixed-size Box instead (see icon-library-iconsax memory).
const ixBox = (size) => ({
  width: size, height: size, flexShrink: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
});

// Same "button-shaped, not pill" badge formula as KnowledgeSourcesTab.jsx's
// StatusChip/CHIP_SX (Rahim, 2026-09-04: "not too much corner radius"),
// sized to the finalized 0.75rem badge tier (font-sizing-scale memory).
const CHIP_SX = { height: 22, fontSize: "0.75rem", fontWeight: 600, borderRadius: "6px" };

// Same card shell as AgentsTab.jsx's AgentCard — border/radius/background/
// hover formula, so a feature row here reads as the same kind of surface as
// an agent card elsewhere on this page (Rahim, 2026-09-05: "match the ui in
// other sections").
const featureCardSx = {
  position: "relative",
  borderRadius: "16px",
  border: "1.5px solid",
  borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.15)"),
  bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "transparent"),
  transition: "border-color 0.18s",
  "&:hover": {
    borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.26)" : "rgba(0,0,0,0.25)"),
  },
};

// Colored 40px icon square — same shell AgentCard's own 36px icon square
// uses (radius/flex-centering), just with a solid tint behind the iconsax
// glyph instead of a plain emoji, since each row here needs to read as its
// own distinct feature at a glance.
function FeatureIcon({ Icon, color = "primary" }) {
  return (
    <Box
      sx={{
        width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: (theme) => theme.palette[color].main,
        color: (theme) => theme.palette[color].contrastText,
      }}
    >
      <Box sx={ixBox(20)}><Icon size="100%" color="currentColor" variant="Bold" /></Box>
    </Box>
  );
}

// Placeholder rows — UI-only for now, no backend behind either. Not
// rendered in the frontend at all right now (Rahim, 2026-09-05: "just dont
// call them in frontend") — data kept here, commented, so re-enabling later
// is just uncommenting this plus the render block below.
// const COMING_SOON_CARDS = [
//   {
//     key: "sentimentAnalysis",
//     Icon: EmojiHappy,
//     title: "Sentiment Analysis",
//     description: "Automatically detect customer sentiment and flag urgent or negative conversations.",
//   },
//   {
//     key: "autoTranslation",
//     Icon: Translate,
//     title: "Auto Translation",
//     description: "Translate incoming messages to your team's language and outgoing replies back to the customer's language.",
//   },
// ];

const DEFAULT_SETTINGS = { autoSuggestEnabled: false, refineEnabled: false, takeoverEnabled: false, agentId: "", historyTurns: 8 };

export default function InboxAiTab({ workspaceId, agents = [] }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null); // which field is mid-save, for per-control spinners
  // Was a bare window.confirm() (the raw browser "localhost:3000 says"
  // dialog) gating the AI Takeover toggle — same bug already fixed for
  // delete-agent/delete-source elsewhere on this page (Rahim, 2026-09-04
  // audit). Styled confirm dialog instead, same title+close/body/Cancel-
  // Confirm shape.
  const [takeoverConfirmOpen, setTakeoverConfirmOpen] = useState(false);
  // featureRequest local state removed — only used by the commented-out
  // "Request a Feature" section below (Rahim, 2026-09-05), re-add alongside
  // it when re-enabled.
  const confirmTakeoverEnable = () => {
    setTakeoverConfirmOpen(false);
    persist({ takeoverEnabled: true }, "takeoverEnabled");
  };

  const loadSettings = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.message || "Failed to load Inbox AI settings"); return; }
      setSettings({
        autoSuggestEnabled: !!data.inboxAssist?.autoSuggestEnabled,
        refineEnabled: !!data.inboxAssist?.refineEnabled,
        takeoverEnabled: !!data.inboxAssist?.takeoverEnabled,
        agentId: data.inboxAssist?.agentId ? String(data.inboxAssist.agentId) : "",
        historyTurns: data.inboxAssist?.historyTurns || 8,
      });
    } catch (err) {
      console.error("Failed to load Inbox AI settings", err);
      toast.error("Failed to load Inbox AI settings");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Optimistic: apply locally first, PATCH the single changed field, roll back on failure.
  const persist = useCallback(async (patch, savingKeyName) => {
    const previous = settings;
    setSettings((prev) => ({ ...prev, ...patch }));
    setSavingKey(savingKeyName);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) {
        setSettings(previous);
        toast.error(data?.message || "Failed to save");
        return;
      }
      setSettings({
        autoSuggestEnabled: !!data.inboxAssist?.autoSuggestEnabled,
        refineEnabled: !!data.inboxAssist?.refineEnabled,
        takeoverEnabled: !!data.inboxAssist?.takeoverEnabled,
        agentId: data.inboxAssist?.agentId ? String(data.inboxAssist.agentId) : "",
        historyTurns: data.inboxAssist?.historyTurns || 8,
      });
    } catch (err) {
      console.error("Failed to save Inbox AI settings", err);
      setSettings(previous);
      toast.error("Failed to save");
    } finally {
      setSavingKey(null);
    }
  }, [settings, workspaceId]);

  const hasAgent = !!settings.agentId;
  const noAgentsAvailable = agents.length === 0;

  return (
    <Box>
      {/* Own "Inbox AI" title + explainer paragraph both dropped (Rahim,
          2026-09-05: "no need for this") — redundant now that this is a
          real drill-down page (see AiSettings.jsx), same as
          KnowledgeSourcesTab.jsx dropping its own duplicate title once the
          page's top PanelHeading started reflecting the open tab. */}
      {noAgentsAvailable && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: "8px", fontSize: "0.8125rem" }}>
          Create an AI Agent under the "Agents" tab first — Inbox AI reuses an existing agent's instructions
          and knowledge sources to draft/refine replies.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Which agent powers both features below — plain block, no card
            border (Rahim, 2026-09-05: "dont make the inbox assist agent
            content in a card remove the border colour"). No section title
            above it (tried, rejected: "ok so not this remove it"), and the
            sub-para under the row was tried too and also rejected (Rahim:
            "remove it") — just the label + dropdown now. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LabeledField label="Reply Assistant" sx={{ width: 280 }}>
            <Select
              size="small"
              displayEmpty
              disabled={loading || noAgentsAvailable}
              value={settings.agentId}
              onChange={(e) => persist({ agentId: e.target.value }, "agentId")}
              MenuProps={inboxSelectMenuProps}
              sx={inboxSelectSx}
            >
              <MenuItem value="" sx={inboxMenuItemSx}><em>None selected</em></MenuItem>
              {agents.map((a) => (
                <MenuItem key={a._id} value={a._id} sx={inboxMenuItemSx}>{a.name}{a.isDefault ? " (Default)" : ""}</MenuItem>
              ))}
            </Select>
          </LabeledField>
          {savingKey === "agentId" && <CircularProgress size={16} />}
        </Box>

        <Paper elevation={0} sx={{ ...featureCardSx, p: 2.75, display: "flex", alignItems: "flex-start", gap: 2 }}>
          <FeatureIcon Icon={Magicpen} color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>Suggest Reply</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Draft a reply from the last few messages in the conversation plus the selected agent's training —
              the human agent reviews and edits it in the compose box before sending.
            </Typography>
          </Box>
          <PillSwitch
            checked={settings.autoSuggestEnabled}
            disabled={loading || savingKey === "autoSuggestEnabled" || (!hasAgent && !settings.autoSuggestEnabled)}
            onChange={(e) => persist({ autoSuggestEnabled: e.target.checked }, "autoSuggestEnabled")}
          />
        </Paper>

        <Paper elevation={0} sx={{ ...featureCardSx, p: 2.75, display: "flex", alignItems: "flex-start", gap: 2 }}>
          <FeatureIcon Icon={MessageEdit} color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>Refine</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Let the agent rewrite whatever a team member has already typed in the compose box — clearer,
              on-brand wording, same meaning. Only available once there's text in the box.
            </Typography>
          </Box>
          <PillSwitch
            checked={settings.refineEnabled}
            disabled={loading || savingKey === "refineEnabled" || (!hasAgent && !settings.refineEnabled)}
            onChange={(e) => persist({ refineEnabled: e.target.checked }, "refineEnabled")}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...featureCardSx, p: 2.75, display: "flex", alignItems: "flex-start", gap: 2,
            ...(settings.takeoverEnabled && { borderColor: (theme) => theme.palette.warning.main }),
          }}
        >
          <FeatureIcon Icon={Cpu} color="warning" />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>AI Takeover</Typography>
              <Chip label="Auto-sends" size="small" color="warning" variant="outlined" sx={CHIP_SX} />
            </Box>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Lets a team member hand a whole conversation to AI directly from the Inbox compose box
              ("Assign to AI"). Unlike Suggest Reply and Refine above, the AI replies to the customer
              automatically — no human review — until the conversation is reassigned to a team member or a
              team member turns it off.
            </Typography>
          </Box>
          <PillSwitch
            checked={settings.takeoverEnabled}
            disabled={loading || savingKey === "takeoverEnabled" || (!hasAgent && !settings.takeoverEnabled)}
            onChange={(e) => {
              const next = e.target.checked;
              if (next) { setTakeoverConfirmOpen(true); return; }
              persist({ takeoverEnabled: false }, "takeoverEnabled");
            }}
          />
        </Paper>

        {/* Temporarily disabled (Rahim, 2026-09-05) — not rendered in the
            frontend for now, will re-enable later. COMING_SOON_CARDS is
            commented out above too, re-add both together. */}
        {/* {COMING_SOON_CARDS.map(({ key, Icon, title, description }) => (
          <Paper
            key={key}
            elevation={0}
            sx={{
              ...featureCardSx, p: 2.75, display: "flex", alignItems: "flex-start", gap: 2,
              opacity: 0.75, transition: "opacity 0.2s, border-color 0.18s", "&:hover": { opacity: 1 },
            }}
          >
            <FeatureIcon Icon={Icon} color="primary" />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>{title}</Typography>
                <Chip label="Coming Soon" size="small" variant="outlined" sx={CHIP_SX} />
              </Box>
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>{description}</Typography>
            </Box>
          </Paper>
        ))} */}
      </Box>

      {/* Temporarily disabled (Rahim, 2026-09-05) — not rendered in the
          frontend for now, will re-enable later.
      <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary", mb: 0.5 }}>
          Request a Feature
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 1.5 }}>
          Have an idea for an Inbox AI feature? Let us know.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <ExpandableParagraphField
              label=""
              placeholder="Describe the AI feature you'd like…"
              value={featureRequest}
              onChange={setFeatureRequest}
              minRows={2}
            />
          </Box>
          <Button variant="outlined" sx={{ ...neutralButtonSx, fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
            <Box sx={{ ...ixBox(16), mr: 0.75 }}><MessageQuestion size="100%" color="currentColor" variant="Linear" /></Box>
            Submit
          </Button>
        </Box>
      </Box>
      */}

      {/* AI Takeover enable confirmation — same title+close/body/Cancel-
          Confirm shape as this page's other confirm dialogs
          (KnowledgeSourcesTab.jsx's delete confirmations). */}
      <Dialog
        open={takeoverConfirmOpen}
        onClose={() => setTakeoverConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 600, color: "text.primary", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2.25 }}>
          Enable AI Takeover
          <IconButton size="small" onClick={() => setTakeoverConfirmOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
          <Box sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              When enabled, team members can hand entire conversations to AI. The AI will reply to customers
              automatically with no review step. Continue?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", justifyContent: "space-between" }}>
          <Button
            onClick={() => setTakeoverConfirmOpen(false)}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", color: (theme) => theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmTakeoverEnable}
            variant="outlined"
            sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
